import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BillingPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useApi } from "../hooks/useApi.js";
import { AsyncBoundary, InlineMessage, errorText } from "../ui/Feedback.jsx";

export default function BillingScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const query = useApi(() => api.billing.get(), [api]);
  const [message, setMessage] = useState(null); // { tone, text }
  const [chooserOpen, setChooserOpen] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);

  async function handleUpdatePayment() {
    setMessage(null);
    try {
      const result = await api.billing.updatePayment();
      if (result?.portalUrl) {
        // The card is entered at the provider, never here.
        window.location.assign(result.portalUrl);
        return;
      }
      setMessage({ tone: "success", text: "Payment method updated." });
    } catch (err) {
      if (err?.isNotConfigured) {
        // 503 not_configured — say so plainly; never pretend a card was changed.
        setMessage({
          tone: "warning",
          text:
            err.message ||
            "No payment provider is configured on this server yet, so no card can be stored or charged. Plan and usage data below is still accurate.",
        });
      } else {
        setMessage({ tone: "danger", text: errorText(err, "Could not update the payment method.") });
      }
    }
  }

  // A priced tier is bought in two steps. This one picks the tier; the period
  // screen picks the month or the year, because that is what decides which price
  // the checkout is created for. Sending the customer straight to a checkout from
  // here would have to assume a period, and assuming one is how somebody who
  // wanted a year is charged for a month.
  function handleChoosePlan(planId) {
    navigate(`/billing/plan/${planId}`);
  }

  // The free tier is the one plan switched locally: there is nothing to charge
  // for, so there is no period to choose and no checkout to open.
  async function handleSwitchToFree(planId) {
    setBusyPlan(planId);
    setMessage(null);
    try {
      const result = await api.billing.changePlan(planId);
      if (result?.checkoutUrl) {
        // A priced plan changes only once the provider confirms payment, and the
        // response carries no plan. Saying "Plan updated" here would be the most
        // damaging lie this screen could tell, so send the browser to the hosted
        // checkout instead.
        setMessage({ tone: "success", text: "Redirecting to the payment provider…" });
        window.location.assign(result.checkoutUrl);
        return;
      }
      setChooserOpen(false);
      setMessage({ tone: "success", text: "Plan updated." });
      query.reload();
    } catch (err) {
      if (err?.code === "quota_exceeded" || err?.code === "conflict") {
        setMessage({
          tone: "warning",
          text: `${errorText(err, "You're over the limit for that plan.")} Remove feeds first, or pick a larger plan.`,
        });
      } else {
        setMessage({ tone: "danger", text: errorText(err, "Could not change the plan.") });
      }
    } finally {
      setBusyPlan(null);
    }
  }

  async function handleDownloadInvoice(invoiceId) {
    setMessage(null);
    try {
      const blob = await api.billing.downloadInvoice(invoiceId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `invoice-${invoiceId}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err, "Could not download that invoice.") });
    }
  }

  return (
    <div>
      {message ? <InlineMessage tone={message.tone}>{message.text}</InlineMessage> : null}

      <AsyncBoundary query={query} onBack={() => navigate("/")}>
        {(data) => (
          <>
            {!data.paymentsConfigured ? (
              <InlineMessage tone="warning">
                No payment provider is configured on this server, so nothing can be charged. Plan
                and usage data below is still accurate.
              </InlineMessage>
            ) : data.paymentMethod == null ? (
              // Having no card recorded is the normal state before the first
              // checkout, and it says nothing about whether the server can take
              // payments. The card is entered at the provider and never stored
              // here.
              <InlineMessage tone="info">
                No card on file yet. You will be asked for one at the payment provider when you
                buy a paid plan; card details are never stored on this server.
              </InlineMessage>
            ) : null}
            <BillingPage
              plan={data.plan}
              paymentMethod={data.paymentMethod ?? { brand: "None on file", last4: "————", expiry: "—" }}
              invoices={data.invoices}
              onChangePlan={() => setChooserOpen((open) => !open)}
              onUpdatePayment={handleUpdatePayment}
              onDownloadInvoice={handleDownloadInvoice}
            />

            {chooserOpen ? (
              <div className="ical-card" style={{ marginTop: 20 }}>
                <p style={{ margin: "0 0 4px", fontSize: "var(--text-base)", fontWeight: 500 }}>
                  Choose a plan
                </p>
                <p style={{ margin: "0 0 12px", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
                  Paid plans are charged by our payment provider; the plan changes once the payment
                  is confirmed, never before.
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                    gap: 12,
                  }}
                >
                  {data.plans.map((option) => (
                    <PlanCard
                      key={option.id}
                      option={option}
                      contactUrl={data.contactUrl}
                      busy={busyPlan === option.id}
                      onChoose={handleChoosePlan}
                      onSwitchToFree={handleSwitchToFree}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}

/**
 * PlanCard is one tier in the chooser.
 *
 * Three very different outcomes share one card. A contact-only tier is arranged
 * with sales and links to them. A tier this deployment can charge for leads to
 * the period screen, where the month or the year is chosen and the hosted
 * checkout is opened for that price. The free tier is the one local switch,
 * because there is nothing to charge for and therefore no period to pick. A paid
 * tier with no configured price is disabled rather than silently switched: a
 * button that cannot do what it says is worse than one that says it cannot.
 */
function PlanCard({ option, contactUrl, busy, onChoose, onSwitchToFree }) {
  const limit =
    option.feedLimit == null
      ? "Unlimited calendars"
      : `Up to ${option.feedLimit.toLocaleString()} calendars`;

  let action;
  if (option.current) {
    action = <span className="ical-badge ical-badge-success">Current plan</span>;
  } else if (option.contactOnly) {
    action = contactUrl ? (
      <a className="ical-btn" href={contactUrl} style={{ display: "inline-block", textDecoration: "none" }}>
        Contact us
      </a>
    ) : (
      <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Contact us</span>
    );
  } else if (option.purchasable) {
    action = (
      <button
        type="button"
        className="ical-btn ical-btn-primary"
        disabled={busy}
        onClick={() => onChoose(option.id)}
      >
        Choose this plan
      </button>
    );
  } else if (option.id === "free") {
    action = (
      <button
        type="button"
        className="ical-btn"
        disabled={busy}
        onClick={() => onSwitchToFree(option.id)}
      >
        {busy ? "Switching…" : "Switch to Free"}
      </button>
    );
  } else {
    action = (
      <button
        type="button"
        className="ical-btn"
        disabled
        title="No price is configured for this plan on this server"
      >
        Unavailable
      </button>
    );
  }

  return (
    <div className="ical-row" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <p style={{ margin: 0, fontSize: "var(--text-base)", fontWeight: 500 }}>{option.name}</p>
      <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>{limit}</p>
      {option.rateLabel ? (
        <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
          {option.rateLabel}
        </p>
      ) : null}
      <div style={{ marginTop: 4 }}>{action}</div>
    </div>
  );
}
